import { refreshAuthToken, logout } from '../Utils/authUtils';

// Singleton token-refresh promise — if multiple concurrent requests all get 401,
// only one refresh call is made; the others wait on the same promise.
let _tokenRefreshPromise = null;

// Short-lived GET response cache — avoids duplicate network calls for the same URL
// within the same page load (e.g. parent + child both fetching the same resource).
const _getCache = new Map(); // url -> { data, expiry }
const GET_CACHE_TTL = 5000; // 5 seconds

export const fetchData = async (url, options, timeout = 120000) => { // Default timeout set to 2 minutes (120000ms)
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout); // Set the timeout

  const fetchOptions = {
    ...options,
    signal: controller.signal, // Pass the signal to the fetch request
  };

  try {
    const response = await fetch(url, fetchOptions);

    // `/user/login` IS the token-issuing endpoint — never try to refresh
    // tokens for it; just return its body so the caller can handle 401s.
    const isLoginEndpoint = typeof url === 'string' && url.includes('/user/login');

    // Anonymous users never had a token. A 401 for them is simply "this
    // endpoint requires auth" — not a session expiry. Just return the body
    // so the caller can decide what to do (typically: show partial UI and
    // gate the action buttons behind a login prompt). Without this guard,
    // we'd call logout() → window.location.reload() and trap them in a
    // reload loop on any page that calls a protected endpoint.
    const hadAuthToken = !!localStorage.getItem('authToken');

    // Handle 401 Unauthorized - token expired
    if (response.status === 401 && !isLoginEndpoint && hadAuthToken) {
      // Deduplicate: if refresh is already in-flight, wait for it instead of firing another
      if (!_tokenRefreshPromise) {
        _tokenRefreshPromise = refreshAuthToken().finally(() => {
          _tokenRefreshPromise = null;
        });
      }
      const refreshSuccess = await _tokenRefreshPromise;
      if (refreshSuccess) {
        // Retry the original request with new token
        const newOptions = {
          ...options,
          headers: {
            ...options.headers,
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          }
        };
        const retryResponse = await fetch(url, newOptions);
        return await retryResponse.json();
      } else {
        // Refresh failed, redirect to login
        logout();
        throw new Error('Authentication failed');
      }
    }

    return await response.json();
  } catch (error) {
    throw error; // Rethrow the error to let the calling code handle it
  } finally {
    clearTimeout(timeoutId); // Clear the timeout once the request is completed
  }
};

// Helper function to get userId from localStorage
const getUserId = () => {
  const userId = localStorage.getItem('userId') || (JSON.parse(localStorage.getItem('profileObject') || '{}').id);
  // Return default userId for non-authenticated users
  return userId || 'slazysloth_user';
};

// Helper function to check if user is authenticated
const isUserAuthenticated = () => {
  const authToken = localStorage.getItem('authToken');
  const userId = localStorage.getItem('userId') || (JSON.parse(localStorage.getItem('profileObject') || '{}').id);
  return !!(authToken && userId);
};

// URLs that should be blocked for non-authenticated users
const BLOCKED_URLS = {
  // AI-related endpoints
  AI_ENDPOINTS: ['/ai/sync', '/ai/async', '/ai/status'],
  // Today's questions endpoints (block all access for non-authenticated users)
  TODAY_QUESTIONS: ['/core-questions/submit', '/core-questions/submissions', '/core-questions/stats', '/core-questions/problem'],
  // Code execution endpoints (ONLY execution, not problem fetching)
  CODE_EXECUTION: ['/api/lambda/cpp/execute', '/api/lambda/java/execute'],
  // DSA session management (block session creation but allow problem viewing)
  DSA_SESSIONS: ['/session/create-session'],
  // Workspace-related patterns
  WORKSPACE_PATTERNS: ['/lld-attempt/update-metadata', '/hld-attempt/update-metadata', '/lld-attempt/update-drawing', '/hld-attempt/update-drawing', '/ai-answers/lld-step/all', '/ai-answers/hld-step/all', '/notes-questions/get', '/lld-problems/upsert', '/hld-problems/upsert']
};

// Helper function to check if URL should be blocked for non-authenticated users
const shouldBlockUrl = (url) => {
  if (isUserAuthenticated()) {
    return false; // Don't block anything for authenticated users
  }

  const urlPath = new URL(url).pathname;
  const urlString = url.toString();

  // Check AI endpoints
  if (BLOCKED_URLS.AI_ENDPOINTS.some(endpoint => urlPath.includes(endpoint))) {
    return true;
  }

  // Check today's questions endpoints
  if (BLOCKED_URLS.TODAY_QUESTIONS.some(endpoint => urlPath.includes(endpoint))) {
    return true;
  }

  // Check code execution endpoints
  if (BLOCKED_URLS.CODE_EXECUTION.some(endpoint => urlPath.includes(endpoint))) {
    return true;
  }

  // Check DSA session management endpoints
  if (BLOCKED_URLS.DSA_SESSIONS.some(endpoint => urlPath.includes(endpoint))) {
    return true;
  }

  // Check workspace patterns
  if (BLOCKED_URLS.WORKSPACE_PATTERNS.some(pattern => urlPath.includes(pattern))) {
    return true;
  }

  // Check for personal workspace access (userId in URL as identifier)
  if (urlString.includes('&id=slazysloth_user') || urlString.includes('?id=slazysloth_user')) {
    return true;
  }

  return false;
};

// Helper function to show login prompt
const showLoginPrompt = (feature) => {
  // Always show a simple alert message
  alert(`Please login to access ${feature}`);
  
  // Throw an error to prevent the API call
  throw new Error(`Authentication required for ${feature}`);
};

// Helper function to get auth token
const getAuthToken = () => {
  return localStorage.getItem('authToken');
};

// Helper function to add auth headers
const getAuthHeaders = () => {
  const token = getAuthToken();
  const headers = {
    'Content-Type': 'application/json',
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  // Add X-API-Key from environment variable
  if (process.env.REACT_APP_X_API_KEY) {
    headers['x-api-key'] = process.env.REACT_APP_X_API_KEY;
  }
  
  return headers;
};

// Helper function to add userId to URL if not present
const addUserIdToUrl = (url) => {
  const urlObj = new URL(url);
  
  // If user_id or userId is already in URL, don't add default userId
  if (urlObj.searchParams.has('user_id') || urlObj.searchParams.has('userId')) {
    return url;
  }
  
  // Don't add userId for login endpoint - it's handled separately
  if (url.includes('/user/login')) {
    return url;
  }
  
  // Don't add default userId if user is not authenticated
  if (!isUserAuthenticated()) {
    return url;
  }
  
  const userId = getUserId();
  if (!userId || userId === 'slazysloth_user') return url; // Don't add default userId
  
  urlObj.searchParams.append('userId', userId);
  return urlObj.toString();
};

export const postRequest = async (url, bodyObject, timeout = 120000) => { // Default timeout set to 2 minutes
  // Check if URL should be blocked for non-authenticated users
  if (shouldBlockUrl(url)) {
    const urlPath = new URL(url).pathname;
    let feature = 'this feature';
    
    if (urlPath.includes('/ai/')) feature = 'AI assistance';
    else if (urlPath.includes('/core-questions/')) feature = "today's questions";
    else if (urlPath.includes('/api/lambda/')) feature = 'code execution';
    else if (urlPath.includes('/session/create-session')) feature = 'DSA session creation';
    else if (urlPath.includes('/problems/upsert')) feature = 'problem creation';
    else if (urlPath.includes('/attempt/update-metadata') || urlPath.includes('/attempt/update-drawing') || urlPath.includes('/ai-answers/')) feature = 'workspace features';
    
    showLoginPrompt(feature);
    return; // This won't be reached due to the throw in showLoginPrompt
  }

  // Check if this is login endpoint - handle separately
  const isLoginEndpoint = url.includes('/user/login');
  
  const userId = getUserId();
  
  // Add userId to URL if not present (skip for login endpoint)
  const urlWithUserId = addUserIdToUrl(url);
  
  // Add userId to body if not present and bodyObject exists (skip for login endpoint)
  let finalBodyObject = bodyObject;
  if (bodyObject && userId && !bodyObject.userId && !isLoginEndpoint && userId !== 'slazysloth_user') {
    finalBodyObject = { ...bodyObject, userId };
  }
  
  const options = {
    method: 'POST',
    headers: getAuthHeaders(), // Use auth headers
    body: JSON.stringify(finalBodyObject),
  };

  try {
    return await fetchData(urlWithUserId, options, timeout);
  } catch (error) {
    // Handle the error
    throw error;
  }
};

export const deleteRequest = async (url, timeout = 120000) => {
  const urlWithUserId = addUserIdToUrl(url);
  const options = {
    method: 'DELETE',
    headers: getAuthHeaders(),
  };
  try {
    return await fetchData(urlWithUserId, options, timeout);
  } catch (error) {
    throw error;
  }
};

export const getRequest = async (url, timeout = 120000) => { // Default timeout set to 2 minutes
  // Check if URL should be blocked for non-authenticated users
  if (shouldBlockUrl(url)) {
    const urlPath = new URL(url).pathname;
    let feature = 'this feature';

    if (urlPath.includes('/ai/')) feature = 'AI assistance';
    else if (urlPath.includes('/core-questions/')) feature = "today's questions";
    else if (urlPath.includes('/api/lambda/')) feature = 'code execution';
    else if (urlPath.includes('/session/create-session')) feature = 'DSA session creation';
    else if (urlPath.includes('/problems/upsert')) feature = 'problem creation';
    else if (urlPath.includes('/attempt/update-metadata') || urlPath.includes('/attempt/update-drawing') || urlPath.includes('/ai-answers/')) feature = 'workspace features';

    showLoginPrompt(feature);
    return; // This won't be reached due to the throw in showLoginPrompt
  }

  // Add userId to URL if not present
  const urlWithUserId = addUserIdToUrl(url);

  // Check short-lived cache (avoids duplicate fetches for the same URL within 5s)
  const now = Date.now();
  const cached = _getCache.get(urlWithUserId);
  if (cached && cached.expiry > now) {
    return cached.data;
  }

  const options = {
    headers: getAuthHeaders(),
  };

  try {
    const result = await fetchData(urlWithUserId, options, timeout);
    // Cache successful responses only (don't cache errors)
    if (result) {
      _getCache.set(urlWithUserId, { data: result, expiry: now + GET_CACHE_TTL });
    }
    return result;
  } catch (error) {
    throw error;
  }
};
